/* eslint-disable no-console */
import { HtmlTagNames, HtmlTagOptions } from '@novaheal/types';
import { HTMLReactParserOptions, Element, domToReact, DOMNode } from 'html-react-parser';
import { Separator, ListItem, Text, TextProps, Link, List } from '@chakra-ui/react';
import { ArticleImage } from 'components/Article/ArticleImage';
import { ArticleVideo } from 'components/Article/ArticleVideo';
import { Keyword } from 'components/Article/Keyword';
import { ArticleEmbeddedEntry } from 'components/Article/ArticleEmbeddedEntry';

// TODO: streamline font sizes and line heights with textStyles (part of mobile theme optimization)
const basicTextStyle: TextProps = {
  textStyle: 'body.m.regular',
  minHeight: '1em', // prevent empty paragraphs from collapsing
  mt: '10px',
  lineHeight: '130%',
  whiteSpace: 'pre-line', // prevent line breaks from collapsing, see here: https://github.com/contentful/rich-text/issues/96#issuecomment-953729068
};
const textStyles: Partial<Record<HtmlTagNames, TextProps>> = {
  [HtmlTagNames.PARAGRAPH]: {
    ...basicTextStyle,
  },
  [HtmlTagNames.HEADING_1]: {
    as: 'h1',
    fontSize: { base: '18px', md: '20px' },
    mt: '30px',
    lineHeight: '130%',
    fontWeight: 'medium',
  },
  [HtmlTagNames.HEADING_2]: {
    as: 'h2',
    fontSize: { base: '16px', md: '18px' },
    mt: '15px',
    lineHeight: '130%',
    fontWeight: 'medium',
  },
  [HtmlTagNames.BOLD]: { ...basicTextStyle, as: 'span', fontWeight: 'semibold' },
  [HtmlTagNames.SUBSCRIPT]: { ...basicTextStyle, as: 'sub' },
  [HtmlTagNames.CODE]: { ...basicTextStyle, as: 'span', color: 'text.quaternary' },
  [HtmlTagNames.ITALIC]: { ...basicTextStyle, as: 'em' },
  [HtmlTagNames.UNDERLINE]: {
    ...basicTextStyle,
    as: 'mark',
    bgColor: 'background.highlight.yellow',
    fontWeight: 'inherit', // keep explicit styling if multiple marks apply
  },

  // Other headings are currnetly not available within the ctf wysiwyg-editor
  // [HtmlTagNames.HEADING_3]: { as: 'h3', textStyle: 'h4.reg', mt: '16px' },
  // [HtmlTagNames.HEADING_4]: { as: 'h4', textStyle: 'body.m.medium' },
  // [HtmlTagNames.HEADING_5]: { as: 'h5', textStyle: 'body.m.medium' },
  // [HtmlTagNames.HEADING_6]: { as: 'h6', textStyle: 'body.m.medium' },
  // Other HtmlTagNames like UL_LIST, OL_LIST, LIST_ITEM, HR, VIDEO, IMAGE, EMBEDDED_ENTRY, and KEYWORD
  // have unique component requirements and aren't directly mappable to a Text component's style props.
  // [HtmlTagNames.HYPERLINK]: { as: 'a', color: "text.info" },
  // [HtmlTagNames.UL_LIST]: {},
  // [HtmlTagNames.OL_LIST]: {},
  // [HtmlTagNames.LIST_ITEM]: {},
  // [HtmlTagNames.HR]: {},
  // [HtmlTagNames.VIDEO]: {},
  // [HtmlTagNames.IMAGE]: {},
  // [HtmlTagNames.EMBEDDED_ENTRY]: {},
  // [HtmlTagNames.KEYWORD]: {},
};

// Helper function to create options with nesting context
// isInsideListItem tracks if we're currently processing content inside a list item
// olDepth tracks the nesting depth of ordered lists (for proper numbering styles)
const createHtmlToReactOptions = (
  isInsideListItem = false,
  olDepth = 0
): HTMLReactParserOptions => ({
  replace: (domNode) => {
    if (domNode instanceof Element) {
      const styles = textStyles[domNode.tagName as HtmlTagNames];
      const isTextNode = (Object.keys(textStyles) as HtmlTagNames[]).includes(
        domNode.tagName as HtmlTagNames
      );

      if (isTextNode) {
        return (
          <Text {...styles}>
            {domToReact(
              domNode.children as DOMNode[],
              createHtmlToReactOptions(isInsideListItem, olDepth)
            )}
          </Text>
        );
      }
      if (domNode.tagName === HtmlTagNames.UL_LIST) {
        // If we're inside a list item, this list is nested
        const isNested = isInsideListItem;
        return (
          <List.Root as="ul" mt={isNested ? undefined : '10px'} ps={isNested ? '5' : undefined}>
            {domToReact(domNode.children as DOMNode[], createHtmlToReactOptions(false, 0))}
          </List.Root>
        );
      }
      if (domNode.tagName === HtmlTagNames.OL_LIST) {
        // If we're inside a list item, this list is nested - increment depth
        const isNested = isInsideListItem;
        const currentOlDepth = isNested ? olDepth + 1 : 0;

        // Determine list-style-type based on depth
        // Cycles through: decimal (1,2,3), lower-alpha (a,b,c), lower-roman (i,ii,iii), upper-alpha (A,B,C), upper-roman (I,II,III)
        const listStyleTypes = [
          'decimal',
          'lower-alpha',
          'lower-roman',
          'upper-alpha',
          'upper-roman',
        ];
        const listStyleType = listStyleTypes[currentOlDepth % listStyleTypes.length];

        return (
          <List.Root
            as="ol"
            mt={isNested ? undefined : '10px'}
            ps={isNested ? '20px' : undefined}
            style={{ listStyleType }}
          >
            {domToReact(
              domNode.children as DOMNode[],
              createHtmlToReactOptions(false, currentOlDepth)
            )}
          </List.Root>
        );
      }
      if (domNode.tagName === HtmlTagNames.LIST_ITEM) {
        // When processing children of a list item, mark that we're inside a list item
        // Pass along the current olDepth so nested OLs can track their depth
        return (
          <ListItem _marker={{ color: 'inherit' }} marginLeft="18px">
            {domToReact(domNode.children as DOMNode[], createHtmlToReactOptions(true, olDepth))}
          </ListItem>
        );
      }
      if (domNode.tagName === HtmlTagNames.HR) {
        return <Separator my="10px" />;
      }
      if (domNode.tagName === HtmlTagNames.HYPERLINK) {
        const uri = domNode.attribs['data-url'];
        return (
          <Link href={uri} target="_blank" rel="noopener noreferrer" color="text.info">
            {domToReact(
              domNode.children as DOMNode[],
              createHtmlToReactOptions(isInsideListItem, olDepth)
            )}
          </Link>
        );
      }
      if (domNode.tagName === HtmlTagNames.EMBEDDED_ENTRY) {
        const entryOptions: HtmlTagOptions[HtmlTagNames.EMBEDDED_ENTRY] = {
          id: domNode.attribs['data-id'],
        };

        return <ArticleEmbeddedEntry id={entryOptions.id} />;
      }
      if (domNode.tagName === HtmlTagNames.IMAGE || domNode.tagName === 'img') {
        const imageOptions: HtmlTagOptions[HtmlTagNames.IMAGE] = {
          url: domNode.attribs['data-url'],
          title: domNode.attribs['data-title'],
          description: domNode.attribs['data-description'],
        };

        const shouldShowImage =
          imageOptions.title !== 'undefined' &&
          imageOptions.description !== 'undefined' &&
          !!imageOptions.title &&
          !!imageOptions.description;

        return shouldShowImage ? (
          <ArticleImage
            imgUrl={imageOptions.url}
            title={imageOptions.title}
            description={imageOptions.description}
          />
        ) : null;
      }
      if (domNode.tagName === HtmlTagNames.VIDEO) {
        const videoOptions: HtmlTagOptions[HtmlTagNames.VIDEO] = {
          title: domNode.attribs['data-title'],
          description: domNode.attribs['data-description'],
          url: domNode.attribs['data-url'],
        };
        return (
          <ArticleVideo
            rawVideoUrl={videoOptions.url}
            // thumbnailUrl={videoOptions.thumbnail?.url}
            description={videoOptions.description}
            // isEmbedded
          />
        );
      }
      if (domNode.tagName === HtmlTagNames.KEYWORD) {
        const options: HtmlTagOptions[HtmlTagNames.KEYWORD] = {
          id: domNode.attribs['data-id'],
        };

        return (
          <Keyword
            title={
              domToReact(
                domNode.children as DOMNode[],
                createHtmlToReactOptions(isInsideListItem, olDepth)
              ) as string
            }
            // marks={node.content[0].marks.map((mark: { type: MARKS }) => mark.type)}
            marks={[]}
            id={options.id}
          />
        );
      }
      if (domNode.tagName === 'div') {
        return (
          <>
            {domToReact(
              domNode.childNodes as DOMNode[],
              createHtmlToReactOptions(isInsideListItem, olDepth)
            )}
          </>
        );
      }
    }
    if (domNode.type !== 'text') {
      console.warn(`Missing implementation for type "${domNode.type}"`, domNode);
    }
  },
});

export const htmlToReactOptions = createHtmlToReactOptions();
